import React, { useState } from 'react';
import { LogOut } from 'lucide-react';
import apiClient from '../services/api';

/**
 * Reusable session controls — drop into ANY form/table that lists users.
 *
 * Requirements:
 *   - the row data must include: userid, fullname (or username)
 *   - for the badge: active_session_id, last_activity, idle_seconds
 *     (add these columns to the form's PHP GET query — see notes)
 *
 * Usage:
 *   <SessionStatusBadge row={access} />
 *   <ForceLogoutButton row={access} onDone={fetchAccessList} />
 */

// Must match SESSION_STALE_MINUTES in login.php (2 min = 120s)
const STALE_SECONDS = 120;

export const getSessionStatus = (row) => {
  const hasSession = row.active_session_id && row.active_session_id !== 'FORCED';
  const idle = row.idle_seconds != null ? parseInt(row.idle_seconds) : null;
  const isLive = hasSession && idle !== null && idle < STALE_SECONDS;
  if (isLive)      return { label: 'Online',  online: true,  cls: 'bg-green-50 text-green-700 border-green-300' };
  if (hasSession)  return { label: 'Stale',   online: false, cls: 'bg-amber-50 text-amber-700 border-amber-300' };
  return             { label: 'Offline', online: false, cls: 'bg-gray-50 text-gray-500 border-gray-200' };
};

export const SessionStatusBadge = ({ row }) => {
  const s = getSessionStatus(row);
  return (
    <div>
      <span className={`inline-block px-2.5 py-1 border-2 rounded-lg text-[12px] font-bold ${s.cls}`}>
        {s.label}
      </span>
      {row.last_activity && (
        <p className="text-[11px] text-gray-400 mt-1 m-0 whitespace-nowrap">
          {new Date(row.last_activity.replace(' ', 'T')).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export const ForceLogoutButton = ({ row, onDone }) => {
  const [busy, setBusy] = useState(false);
  const status = getSessionStatus(row);

  // Nothing to kick — render nothing so tables stay clean
  if (status.label === 'Offline') return null;

  const handleClick = async () => {
    const name = row.fullname || row.username || `user #${row.userid}`;
    if (!window.confirm(`Force logout ${name}?\n\nTheir session ends within a minute and they can log in again immediately.`)) return;

    try {
      setBusy(true);
      const res = await apiClient.post('/force-logout.php', { userid: row.userid });
      if (res.data.success) {
        alert(res.data.message);
        onDone?.();
      } else {
        alert('Error: ' + res.data.message);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error forcing logout. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      title="Force Logout (release session)"
      className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer
        bg-amber-100 border-amber-400 text-amber-700 transition-all duration-150
        hover:bg-amber-500 hover:text-white hover:scale-110 hover:-translate-y-0.5
        disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
    >
      <LogOut size={13} />
    </button>
  );
};

export default ForceLogoutButton;